import { DateTime } from "luxon";

// Date filters cover whole days: from the start of the From day to the start of the day after To,
// in the site's time zone when there is one, otherwise the browser's.
const startOfDay = (date: Date, zoneId?: string) =>
  DateTime.fromJSDate(date)
    .setZone(zoneId || "local", { keepLocalTime: true })
    .startOf("day");

export const fromDayStartIso = (date: Date, zoneId?: string): string =>
  startOfDay(date, zoneId).toUTC().toISO() ?? "";

export const toDayEndExclusiveIso = (date: Date, zoneId?: string): string =>
  startOfDay(date, zoneId).plus({ days: 1 }).toUTC().toISO() ?? "";

// The reverse of the two above, for showing an applied range in the pickers again: the day an ISO
// instant falls on in the zone, as a local date.
const dayInPicker = (iso: string, zoneId?: string) =>
  DateTime.fromISO(iso)
    .setZone(zoneId || "local")
    .setZone("local", { keepLocalTime: true })
    .startOf("day");

export const dayFromStartIso = (iso: string, zoneId?: string): Date =>
  dayInPicker(iso, zoneId).toJSDate();

export const dayFromEndExclusiveIso = (iso: string, zoneId?: string): Date =>
  dayInPicker(iso, zoneId).minus({ days: 1 }).toJSDate();

// The pickers open on the last 7 days, so the default range is never a single day by accident.
export const defaultFromDay = (): Date =>
  DateTime.local().startOf("day").minus({ days: 6 }).toJSDate();

// From has to be a day before To: a reversed range and one where both are the same day are both
// refused, each with its own message. Undefined when the range is fine.
export const getDayRangeError = (from: Date, to: Date): string | undefined => {
  const fromDay = DateTime.fromJSDate(from).startOf("day");
  const toDay = DateTime.fromJSDate(to).startOf("day");

  if (fromDay > toDay) {
    return "From date cannot be later than To date";
  }
  if (fromDay.equals(toDay)) {
    return "From and To date cannot be the same";
  }
  return undefined;
};
